import { useEffect, useState } from "react";
import { AppShell, Group, Text, NavLink, Box, Title, Button, ActionIcon, Burger, Badge } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMsal } from "@azure/msal-react";
import { MdOutlineAutoAwesome, MdChat, MdThumbUp, MdOutlineHistory, MdGroup, MdSearch, MdOutlineLightbulb } from "react-icons/md";
import { Outlet, useLocation, Link } from "react-router-dom";
import { ENVIRONMENT_BANNER_HEIGHT, EnvironmentBanner, useNonProdEnvironment } from "../../components/environment-banner/EnvironmentBanner";
import { getToken, useLogin } from "../../authConfig";
import { getFeedbackApi, getIdeasApi } from "../../api";

/** Fetches a pending-review count and refreshes it on every admin navigation; undefined until loaded or on failure. */
const usePendingCount = (pathname: string, fetchCount: (idToken: string, signal: AbortSignal) => Promise<number>, description: string): number | undefined => {
    const client = useLogin ? useMsal().instance : undefined;
    const [count, setCount] = useState<number>();

    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            try {
                const idToken = client ? await getToken(client) : undefined;
                if (!idToken) return;
                setCount(await fetchCount(idToken, controller.signal));
            } catch (err) {
                if (!controller.signal.aborted) console.error(`Failed to load ${description} count`, err);
            }
        })();
        return () => controller.abort();
    }, [client, pathname]);

    return count;
};

/** Negative feedback items still awaiting review. */
const fetchPendingFeedbackCount = async (idToken: string, signal: AbortSignal) =>
    (await getFeedbackApi(idToken, 1, 1, "timestamp", "desc", undefined, undefined, undefined, undefined, undefined, "not_reviewed", undefined, signal))
        .total_items;

/** Confirmed ideas still in review status "new". */
const fetchNewIdeasCount = async (idToken: string, signal: AbortSignal) =>
    (await getIdeasApi(idToken, 1, 1, "timestamp", "desc", undefined, undefined, undefined, undefined, undefined, undefined, false, "new", signal)).total_items;

const pendingCountBadge = (count: number | undefined, title: string) =>
    count ? (
        <Badge size="sm" variant="filled" color="red" circle={count < 10} title={title}>
            {count > 99 ? "99+" : count}
        </Badge>
    ) : undefined;

const AdminLayout = () => {
    const location = useLocation();
    const [mobileNavOpened, { toggle }] = useDisclosure();
    const nonProdEnvironment = useNonProdEnvironment();
    const headerHeight = 100 + (nonProdEnvironment ? ENVIRONMENT_BANNER_HEIGHT : 0);
    const pendingFeedbackCount = usePendingCount(location.pathname, fetchPendingFeedbackCount, "pending feedback review");
    const newIdeasCount = usePendingCount(location.pathname, fetchNewIdeasCount, "new ideas");

    return (
        <AppShell
            header={{ height: headerHeight }}
            navbar={{
                width: 250,
                breakpoint: "sm",
                collapsed: { mobile: !mobileNavOpened }
            }}
            padding="md"
            style={{ backgroundColor: "white", height: "100vh", overflow: "hidden" }}
        >
            {/* HEADER */}
            <AppShell.Header>
                {nonProdEnvironment && (
                    <Box h={ENVIRONMENT_BANNER_HEIGHT}>
                        <EnvironmentBanner environment={nonProdEnvironment} />
                    </Box>
                )}
                <Group h={100} px="md" justify="space-between">
                    <Group gap="sm" justify="center" align="center">
                        <Burger opened={mobileNavOpened} onClick={toggle} hiddenFrom="sm" size="sm" />
                        <Box w={42} h={42} display="flex">
                            <MdOutlineAutoAwesome size={42} />
                        </Box>
                        <Title order={1} c="dark.8" size="h3" visibleFrom="xs">
                            Arthrex Intelligence Admin
                        </Title>
                        <Title order={1} c="dark.8" size="h4" hiddenFrom="xs">
                            Admin
                        </Title>
                    </Group>

                    {/* Back to Chat Button */}
                    <Group gap="xs">
                        <Button component={Link} to="/" variant="subtle" color="gray" leftSection={<MdChat size={16} />} size="sm" visibleFrom="xs">
                            Back to Chat
                        </Button>
                        <ActionIcon component={Link} to="/" variant="subtle" color="gray" size="lg" hiddenFrom="xs">
                            <MdChat size={16} />
                        </ActionIcon>
                    </Group>
                </Group>
            </AppShell.Header>

            {/* SIDEBAR */}
            <AppShell.Navbar p="sm">
                <Text fw={500} mb="sm">
                    Administration
                </Text>
                <NavLink
                    component={Link}
                    to="/administration/feedback"
                    label="Feedback"
                    leftSection={<MdThumbUp size={16} />}
                    rightSection={pendingCountBadge(pendingFeedbackCount, "Feedback pending review")}
                    active={location.pathname.includes("/administration/feedback")}
                    onClick={() => mobileNavOpened && toggle()}
                />
                <NavLink
                    component={Link}
                    to="/administration/ideas"
                    label="Ideas"
                    leftSection={<MdOutlineLightbulb size={16} />}
                    rightSection={pendingCountBadge(newIdeasCount, "New ideas pending review")}
                    active={location.pathname.includes("/administration/ideas")}
                    onClick={() => mobileNavOpened && toggle()}
                />
                <NavLink
                    component={Link}
                    to="/administration/history"
                    label="History"
                    leftSection={<MdOutlineHistory size={16} />}
                    active={location.pathname.includes("/administration/history")}
                    onClick={() => mobileNavOpened && toggle()}
                />
                <NavLink
                    component={Link}
                    to="/administration/users"
                    label="Users"
                    leftSection={<MdGroup size={16} />}
                    active={location.pathname.includes("/administration/users")}
                    onClick={() => mobileNavOpened && toggle()}
                />
                <NavLink
                    component={Link}
                    to="/administration/azure-search"
                    label="Azure AI Search"
                    leftSection={<MdSearch size={16} />}
                    active={location.pathname.includes("/administration/azure-search")}
                    onClick={() => mobileNavOpened && toggle()}
                />
            </AppShell.Navbar>

            {/* MAIN CONTENT */}
            <AppShell.Main
                style={{
                    minWidth: 0,
                    height: `calc(100vh - ${headerHeight}px)`,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}
            >
                <Box
                    style={{
                        minWidth: 0,
                        width: "100%",
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto"
                    }}
                >
                    <Outlet />
                </Box>
            </AppShell.Main>
        </AppShell>
    );
};

export default AdminLayout;
